import { useState, useEffect, useCallback } from "react";
import ModalBox from "../../components/ModalBox/ModalBox";
import "./RapidClicker.css";
import ActivityHeader from "../../components/ActivityHeader/ActivityHeader";
import { modesInfo, initialModal, endGameModal, GAME_DURATION } from "./Modals";
import { trackGameStart } from "../../utils/trackEvents";

const BUTTON_SIZE = { width: 150, height: 50 };

function RapidClicker() {
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [clickCount, setClickCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [modalContent, setModalContent] = useState(initialModal(modesInfo[0]));
  const [selectedMode, setSelectedMode] = useState(0);
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [velocity, setVelocity] = useState({ vx: 2, vy: 2 });
  const [floatingClicks, setFloatingClicks] = useState([]);

  const getParentDimensions = useCallback(() => {
    const parent = document.querySelector(".rapidClicker-content");
    return { width: parent.offsetWidth, height: parent.offsetHeight };
  }, []);

  useEffect(() => {
    if (!isGameStarted || timeLeft === null) return;
    const timerId = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timerId);
  }, [isGameStarted, timeLeft]);

  useEffect(() => {
    if (timeLeft !== 0) return;
    setIsGameStarted(false);
    setModalContent(endGameModal(modesInfo[selectedMode], clickCount));
    setIsModalOpen(true);
  }, [timeLeft, selectedMode, clickCount]);

  useEffect(() => {
    if (!isGameStarted) return;
    let moveId;
    if (selectedMode === 1) {
      moveId = setInterval(() => {
        const { width, height } = getParentDimensions();
        setPosition(({ x, y }) => {
          let vx = velocity.vx,
            vy = velocity.vy;
          let newX = x + vx;
          let newY = y + vy;
          if (newX <= 0 || newX >= width - BUTTON_SIZE.width) vx = -vx;
          if (newY <= 0 || newY >= height - BUTTON_SIZE.height) vy = -vy;
          setVelocity({ vx, vy });
          return {
            x: Math.min(Math.max(newX, 0), width - BUTTON_SIZE.width),
            y: Math.min(Math.max(newY, 0), height - BUTTON_SIZE.height),
          };
        });
      }, 16);
    } else if (selectedMode === 2) {
      moveId = setInterval(() => {
        const { width, height } = getParentDimensions();
        setPosition({
          x: Math.random() * (width - BUTTON_SIZE.width),
          y: Math.random() * (height - BUTTON_SIZE.height),
        });
      }, 3000);
    }
    return () => clearInterval(moveId);
  }, [isGameStarted, selectedMode, velocity, getParentDimensions]);

  function handleStart() {
    setModalContent(initialModal(modesInfo[selectedMode]));
    const { width, height } = getParentDimensions();
    setIsGameStarted(true);
    setTimeLeft(null);
    setClickCount(0);
    setIsModalOpen(false);
    setPosition({
      x: (width - BUTTON_SIZE.width) / 2,
      y: (height - BUTTON_SIZE.height) / 2,
    });
    trackGameStart("RapidClicker", selectedMode);
  }

  function handleClick(e) {
    if (!isGameStarted) return;

    if (timeLeft === null) {
      setTimeLeft(GAME_DURATION);
    }

    setClickCount((c) => c + 1);
    const btn = e.currentTarget;
    btn.classList.add("clicked");
    setTimeout(() => btn.classList.remove("clicked"), 200);

    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const id = Date.now();
    setFloatingClicks((prev) => [...prev, { id, x: offsetX, y: offsetY }]);
    setTimeout(() => {
      setFloatingClicks((prev) => prev.filter((c) => c.id !== id));
    }, 600);
  }


  function handleModeChange(index) {
    setSelectedMode(index);
    setModalContent(initialModal(modesInfo[index]));
    setIsModalOpen(true);
    setIsGameStarted(false);
  }

  return (
    <div className="rapidClicker-main">
      <ActivityHeader>
        {modesInfo.map((m, i) => (
          <div
            key={i}
            className={`rapidClicker-tab ${selectedMode === i ? "active" : ""}`}
            onClick={() => handleModeChange(i)}
          >
            {m.name}
          </div>
        ))}
      </ActivityHeader>

      <ActivityHeader>
        <h3>Time Left: {timeLeft}s</h3>
        <h3>Clicks: {clickCount}</h3>
      </ActivityHeader>

      <div className="rapidClicker-content">
        {isGameStarted ? (
          <button
            className={`click-button ${selectedMode === 0 ? "centered" : ""}`}
            onClick={handleClick}
            style={
              selectedMode > 0 ? { left: position.x, top: position.y } : {}
            }
          >
            Click Me!
            {floatingClicks.map((fc) => (
              <div
                key={fc.id}
                className="floating-plus"
                style={{ left: fc.x, top: fc.y }}
              >
                +1
              </div>
            ))}
          </button>
        ) : (
          <button className="start-button" onClick={() => setIsModalOpen(true)}>
            Start Clicking!
          </button>
        )}
      </div>

      <ModalBox
        isOpen={isModalOpen}
        title={modalContent.title}
        description={modalContent.description}
        buttonText={modalContent.buttonText}
        buttonType={modalContent.buttonType}
        onClose={handleStart}
      />
    </div>
  );
}

export default RapidClicker;
